import { useGetSocketToken } from '@/hooks/use-web-socket/use-get-socket-token';
import { closeSocket } from '@/hooks/use-web-socket/utils';
import { useWindowKeeper } from '@/hooks/useWindowKeeper';
import { removeFromLocalStorage } from '@/lib/local-storage/local-storage';
import { typeGuard } from '@/lib/utils';
import { BASE_BACKEND_URL } from '@/requests/constants';
import { useQueryClient } from '@tanstack/react-query';
import { SESSION_KEY } from '../constants';
import { getSessionValues } from '../get-session-values';

interface LogoutResult {
  readonly success: boolean;
  /** For SAML logout with noRedirect=true, contains the IDP logout URL */
  readonly redirectUrl?: string;
}

/**
 * Calls the backend logout endpoint to invalidate the authentication token.
 * Uses GET method due to SAML compatibility requirements (HTTP-Redirect binding).
 */
async function logoutFromBackend(): Promise<LogoutResult> {
  const { token, userId, customerId } = getSessionValues();

  if (!token || !userId || !customerId) {
    // No valid session, just return success
    return { success: true };
  }

  const url = new URL(`${BASE_BACKEND_URL}/accounts/users/auth/logout/`);
  // Request JSON response instead of redirect for SAML users
  url.searchParams.set('redirect', 'false');

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Token ${token}`,
        'X-PIGELLO-USER-ID': userId,
        'X-PIGELLO-CUSTOMER-ID': customerId,
      },
    });

    // Handle JSON response (SAML with redirect=false)
    if (response.status === 200) {
      const data = await response.json();
      if (typeGuard<{ location?: string }>(data, 'location')) {
        return { success: true, redirectUrl: data.location };
      }
      console.error('Logout response is not a valid location:', data);
      return { success: true };
    }

    // Standard logout success (204 No Content)
    if (response.status === 204) {
      return { success: true };
    }

    // Other error - treat as success but log the error
    console.error(
      'Logout request failed or unknown status:',
      response.status,
      response.statusText
    );

    // Token already invalid or other error - treat as success
    // since the goal is to log out
    return { success: true };
  } catch (error) {
    console.error('Logout request failed:', error);
    // Even if the request fails, proceed with client-side cleanup
    return { success: true };
  }
}

export function useSignOut() {
  const queryClient = useQueryClient();
  const socketToken = useGetSocketToken();
  const { resetPreviews } = useWindowKeeper();

  return async (triggerBackendLogout = true): Promise<void> => {
    if (!triggerBackendLogout) {
      removeFromLocalStorage({ key: SESSION_KEY });
      if (socketToken) {
        closeSocket(socketToken);
      }
      resetPreviews();
      await queryClient.cancelQueries();
      queryClient.clear();
      window.location.href = '/login';
      return;
    }

    // 1. Call backend to invalidate token
    const result = await logoutFromBackend();

    // 2. Clear client-side state
    removeFromLocalStorage({ key: SESSION_KEY });
    if (socketToken) {
      closeSocket(socketToken);
    }
    resetPreviews();
    await queryClient.cancelQueries();
    queryClient.clear();

    // 3. Handle redirect
    if (result.redirectUrl) {
      // SAML user - redirect to IDP logout
      window.location.href = result.redirectUrl;
    } else {
      // Standard user - redirect to login page
      window.location.href = '/login';
    }
  };
}
